import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import store from "../store";

import { FirebaseApp } from "../auth";

// Components
import Home from "../views/Home.vue";
import Login from "../views/Login.vue";
import Register from "../views/Register.vue";
import RecoverPassword from "../views/RecoverPassword.vue";
import Dashboard from "../views/Dashboard.vue";
import PrognosticList from "../components/PrognosticList.vue";
import TiroCerteiro from "../views/TiroCerteiro.vue";
import Bigometro from "../views/Bigometro.vue";
import Noticias from "../views/Noticias.vue";
import Alertas from "../views/Alertas.vue";
import Settings from "../views/Settings.vue";
// Admin
import BackofficeDashboard from "../views/backoffice/BackofficeDashboard.vue";
import BackofficeBaseDados from "../views/backoffice/BackofficeBaseDados.vue";
import BackofficeInvoices from "../views/backoffice/BackofficeInvoices.vue";
import BackofficeNews from "../views/backoffice/BackofficeNews.vue";
import BackofficeMatches from "../views/backoffice/BackofficeMatches.vue";
import BackofficePrognostics from "../views/backoffice/BackofficePrognostics.vue";
import BackofficeTiroCerteiro from "../views/backoffice/BackofficeTiroCerteiro.vue";
import BackofficeUsers from "../views/backoffice/BackofficeUsers.vue";
import BackofficeWebsite from "../views/backoffice/BackofficeWebsite.vue";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  // PUBLIC
  {
    path: "/home",
    name: "Home",
    component: Home
  },
  {
    path: "/login",
    name: "Login",
    component: Login
  },
  {
    path: "/registo",
    name: "Register",
    component: Register
  },
  {
    path: "/recuperar-password",
    name: "RecoverPassword",
    component: RecoverPassword
  },
  // RESTRICTED
  {
    path: "/",
    name: "Dashboard",
    component: Dashboard,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/configuracoes",
    name: "Settings",
    component: Settings,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/prognosticos",
    name: "Prognósticos",
    component: PrognosticList,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/tiro-certeiro",
    name: "TiroCerteiro",
    component: TiroCerteiro,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/tiro-certeiro/prognosticos",
    name: "PrognosticosTiroCerteiro",
    component: PrognosticList,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/bigometro",
    name: "Bigometro",
    component: Bigometro,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/bigometro/prognosticos",
    name: "PrognosticosBigometro",
    component: PrognosticList,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/noticias",
    name: "Noticias",
    component: Noticias,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/alertas",
    name: "Alertas",
    component: Alertas,
    meta: {
      requiresAuth: true
    }
  },
  // BACKOFFICE
  {
    path: "/backoffice/dashboard",
    name: "BackofficeDashboard",
    component: BackofficeDashboard,
    meta: {
      requiresAdmin: true,
      requiresAuth: true
    }
  },
  {
    path: "/backoffice/jogos",
    name: "BackofficeJogos",
    component: BackofficeMatches,
    meta: {
      requiresAdmin: true,
      requiresAuth: true
    }
  },
  {
    path: "/backoffice/prognosticos",
    name: "BackofficePrognostics",
    component: BackofficePrognostics,
    meta: {
      requiresAdmin: true,
      requiresAuth: true
    }
  },
  {
    path: "/backoffice/tiro-certeiro",
    name: "BackofficeTiroCerteiro",
    component: BackofficeTiroCerteiro,
    meta: {
      requiresAdmin: true,
      requiresAuth: true
    }
  },
  {
    path: "/backoffice/base-de-dados",
    name: "BackofficeBaseDados",
    component: BackofficeBaseDados,
    meta: {
      requiresAdmin: true,
      requiresAuth: true
    }
  },
  {
    path: "/backoffice/faturas",
    name: "BackofficeInvoices",
    component: BackofficeInvoices,
    meta: {
      requiresAdmin: true,
      requiresAuth: true
    }
  },
  {
    path: "/backoffice/utilizadores",
    name: "BackofficeUsers",
    component: BackofficeUsers,
    meta: {
      requiresAdmin: true,
      requiresAuth: true
    }
  },
  {
    path: "/backoffice/website",
    name: "BackofficeWebsite",
    component: BackofficeWebsite,
    meta: {
      requiresAdmin: true,
      requiresAuth: true
    }
  },
  {
    path: "/backoffice/noticias",
    name: "BackofficeNews",
    component: BackofficeNews,
    meta: {
      requiresAdmin: true,
      requiresAuth: true
    }
  },
  {
    path: "/about",
    name: "About",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/About.vue")
  }
];

const router = new VueRouter({
  base: process.env.BASE_URL,
  routes
});

async function getCurrentUser() {
  return new Promise<firebase.User | null>((resolve, reject) => {
    const unsubscribe = FirebaseApp.auth().onAuthStateChanged(user => {
      unsubscribe();
      resolve(user);
    }, reject);
  });
}

async function getIdTokenResult(currentUser: firebase.User | null) {
  return new Promise<firebase.auth.IdTokenResult | null>((resolve, reject) => {
    currentUser?.getIdTokenResult().then(idTokenResult => {
      resolve(idTokenResult);
    }, reject);
  });
}

router.beforeEach(async (to, from, next) => {
  const currentUser: firebase.User | null = await getCurrentUser();
  const isEmailVerified = currentUser != null && currentUser.emailVerified === true;
  const authenticated = currentUser != null && currentUser.isAnonymous === false;
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  let isAdmin = false;

  await currentUser?.getIdTokenResult().then(idTokenResult => {
    if(idTokenResult.claims.roles) {
      if(idTokenResult.claims.roles.includes("ADMIN")) {
        isAdmin = true;
        store.commit("setAdmin", true);
      }
      if(idTokenResult.claims.roles.includes("BIGODDS_PREMIUM")) {
        store.commit("setPremium", true);
      }
      if(idTokenResult.claims.roles.includes("MODERATOR")) {
        store.commit("setModerator", true);
      }
    }
  });

  if (requiresAuth === true && authenticated === false) {
    next("/home");
    return;
  }
  if (requiresAuth === true && authenticated === true && isEmailVerified === false) {
    next("/home");
    return;
  }
  if(requiresAdmin === true && isAdmin === false) {
    next("/");
    return;
  }
  next();
});

export default router;
